import { DocumentData, QuerySnapshot } from "firebase/firestore";
import React, { Fragment, useEffect, useState } from "react";
import {
	Pressable,
	ScrollView,
	StyleSheet,
	TextInput,
	useWindowDimensions,
	View
} from "react-native";
import { useSelector } from "react-redux";

import ChatComponent from "../components/ChatComponent";
import DashboardHeader from "../components/DashboardHeader";
import HomeScreenChatItem from "../components/HomeScreenChatItem";
import { colors } from "../components/Shared";
import ChatsService from "../services/chats";
import CommonService from "../services/common";
import { defaultChat, IChatModel } from "../services/models/chat";
import { RootState } from "../services/store/app";
import { RootStackScreenProps } from "../types";

const chatService = new ChatsService();
const commonService = new CommonService();

export default function DashboardScreen({ navigation }: RootStackScreenProps<"DashBoard">) {
	const common = useSelector((state: RootState) => state.commonReducer);

	const [chats, setChats] = useState<IChatModel[]>([]);
	const [chatSelected, setChatSelected] = useState("");
	const [search, setSearch] = useState("");
	const window = useWindowDimensions();

	const preFilterChats = (chat: IChatModel) => {
		if (chat.ownersId.length === 1) {
			return true;
		}

		if (chat.ownersId.includes(common.user.uid)) {
			return true;
		}

		return false;
	};

	useEffect(() => {
		const subs = chatService.getRealTime((snap: QuerySnapshot<DocumentData>) => {
			const documents = snap.docs.map((doc) => ({
				...doc.data(),
				id: doc.id,
			})) as IChatModel[];

			if (!documents.length) {
				return;
			}
	
			const chatsFiltered = documents.filter(preFilterChats)
				.sort((a, b) => {
					const dateA = a.conversation[a.conversation.length - 1].createAt;
					const dateB = b.conversation[b.conversation.length - 1].createAt;

					return  new Date(dateB).getTime() - new Date(dateA).getTime();
				});

			if (chatsFiltered.length) {
				// setChatSelected(chatsFiltered[0]?.id || "");
				setChats(chatsFiltered);
			}
		});
	}, [common]);

	const isAdmin = common.user.roll > 0;

	const filterChats = (chat: IChatModel) => {
		if (!search) {
			return true;
		}

		if (commonService
			.replaceSpecialChars(chat.target.toLowerCase())
			.match(search.toLowerCase())
		) {
			return true;
		}

		if (chat.ownersData.length > 1) {
			const operator = chat.ownersData.find((owner) => owner.uid !== common.user.uid);
			if (operator) {
				if (commonService
					.replaceSpecialChars(operator.name?.toLowerCase())
					.match(search.toLowerCase())
				) {
					return true;
				}
			}
		}
	};

	return (
		<View style={styles.container}>
			<DashboardHeader
				onPressSettings={() => navigation.navigate("Settings")}
				isAdmin={isAdmin}
			/>

			<View style={styles.content}>
				{window.width > 699 ? (
				<Fragment>
					<View
					style={{
						width: window.width > 699 ? 350 : "100%",
						borderRightWidth: 1,
						borderRightColor: "#c4c4c4",
					}}
					>
					<View style={{ height: 50, padding: 5 }}>
						<TextInput
						style={{
							backgroundColor: colors.bg.medium,
							flex: 1,
							borderRadius: 10,
							paddingLeft: 10,
						}}
						onChangeText={setSearch}
						value={search}
						placeholder="Search"
						/>
					</View>
					<ScrollView style={{ flex: 1 }}>
						{chats?.filter(filterChats).map((chat, index) => (
						<DisplayItemChat
							chat={chat}
							index={index}
							onPress={() => setChatSelected(chat.id || "")}
							user={common.user.uid}
							selected={chatSelected}
							key={index}
						/>
						))}
					</ScrollView>
					</View>

					{chatSelected && (
					<View style={{ flex: 1, backgroundColor: "#e5ddd5" }}>
						<ChatComponent
						chat={
							chats.find(({ id }) => id === chatSelected) || defaultChat
						}
						onBack={() => {
							setChatSelected("");
						}}
						/>
					</View>
					)}
				</Fragment>
				) : (
				<Fragment>
					{!chatSelected && (
					<View
						style={{
						width: window.width > 699 ? 350 : "100%",
						borderRightWidth: 1,
						borderRightColor: "#c4c4c4",
						}}
					>
						<View style={{ height: 50, padding: 5 }}>
						<TextInput
							style={{
							backgroundColor: colors.bg.medium,
							flex: 1,
							borderRadius: 10,
							paddingLeft: 10,
							}}
							onChangeText={setSearch}
							value={search}
							placeholder="Search"
						/>
						</View>
						<ScrollView style={{ flex: 1 }}>
						{chats
							?.filter((chat) => {
							if (!search) {
								return true;
							}
							if (
								commonService
								.replaceSpecialChars(chat.target.toLowerCase())
								.match(search.toLowerCase())
							) {
								return true;
							}
							if (chat.ownersData.length > 1) {
								const operator = chat.ownersData.find(
								(owner) => owner.uid !== common.user.uid
								);
								if (operator) {
								if (
									commonService
									.replaceSpecialChars(operator.name?.toLowerCase())
									.match(search.toLowerCase())
								) {
									return true;
								}
								}
							}
							})
							.map((chat, index) => (
							<DisplayItemChat
								chat={chat}
								index={index}
								onPress={() => setChatSelected(chat.id || "")}
								user={common.user.uid}
								selected={chatSelected}
								key={index}
							/>
							))}
						</ScrollView>
					</View>
					)}

					{chatSelected && (
					<View style={{ flex: 1, backgroundColor: "#e5ddd5" }}>
						<ChatComponent
						chat={
							chats.find(({ id }) => id === chatSelected) || defaultChat
						}
						onBack={() => {
							setChatSelected("");
						}}
						/>
					</View>
					)}
				</Fragment>
				)}
			</View>
		</View>
	);
}

const styles = StyleSheet.create({
	container: {
		flex: 1,
		marginHorizontal: "auto",
		backgroundColor: colors.bg.light,
		width: "100%",
		maxWidth: 1200,
	},
	content: {
		flex: 1,
		flexDirection: "row",
	},
});

interface DisplayItemChatProps {
	chat: IChatModel;
	index: number;
	onPress: any;
	user: string;
	selected: string;
}

const DisplayItemChat = ({ chat, index, onPress, user, selected }: DisplayItemChatProps) => {
	const from = chat.ownersData.find(({ uid }) => uid !== user);

	const lastMessage = chat.conversation[chat.conversation.length - 1];
	const date = new Date(lastMessage.createAt);
	const [name] = from?.name.split(" ") || [];
	
	return (
		<Pressable key={index} onPress={onPress}>
			<HomeScreenChatItem
				type={chat.type}
				selected={selected}
				id={chat.id}
				title={`${chat.target} (${name})`}
				message={lastMessage?.message}
				lastMessageDate={`${date.getHours()}:${date.getMinutes()}`}
				me={chat.lastMessage.uid === user}
				read={chat.lastMessage.read}
				image={chat.picture ? chat.picture : from?.picture ? from.picture : "icon"}
			/>
		</Pressable>
	);
};
